import { Injectable, OnDestroy } from '@angular/core';
import { AbstractHypeService } from '../../../Common/Layout/services/abstract-hype.service';
import { MonitoringRoutingService } from './monitoring-routing.service';
import { BehaviorSubject, Subscription, take } from 'rxjs';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { MonitoringLeftSidebarService } from './monitoring-left-sidebar.service';
import { MediaSubscriptionService } from './media-subscription.service';
import { FolderService } from './folder.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { TopicsService } from './topics.service';
import { topicAgentSystemPrompt } from '../../../api/agents/models/topic-agent-system-prompt.api.model';
import { canvasAgentSystemPrompt } from '../../../api/agents/models/canvas-agent-system-prompt.api.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { AIContextTakeoverComponent } from '../../../Common/Dialogs/components/AIContextTakeover/ai-context-takeover.component';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';


/* eslint-disable @typescript-eslint/naming-convention */
enum HypeType {
    Topics,
    SavedCoverage,
    Subscriptions,
    Briefings,
    Alerts,
    Reports
}

const routeToHypeMap = new Map<RouteType, HypeType>([
    [RouteType.default, HypeType.Topics],
    [RouteType.search, HypeType.Topics],
    [RouteType.updateSearch, HypeType.Topics],
    [RouteType.results, HypeType.Topics],
    [RouteType.topicsHype, HypeType.Topics],
    [RouteType.topic, HypeType.Topics],
    [RouteType.subscriptionsHype, HypeType.Subscriptions],
    [RouteType.subscription, HypeType.Subscriptions],
    [RouteType.subscriptionDashboard, HypeType.Subscriptions],
    [RouteType.folder, HypeType.SavedCoverage],
    [RouteType.mentions, HypeType.SavedCoverage],
    [RouteType.mentionsHype, HypeType.SavedCoverage],
    [RouteType.briefingsHype, HypeType.Briefings],
    [RouteType.briefing, HypeType.Briefings],
    [RouteType.briefingReport, HypeType.Briefings],
    [RouteType.recurringBriefingReport, HypeType.Briefings],
    [RouteType.alertsHype, HypeType.Alerts],
    [RouteType.alert, HypeType.Alerts],
    [RouteType.recurringAlertReport, HypeType.Alerts],
    [RouteType.reportsHype, HypeType.Reports],
    [RouteType.report, HypeType.Reports],
    [RouteType.reportSharedReports, HypeType.Reports],
]);

@Injectable()
export class MonitoringHypeService extends AbstractHypeService implements OnDestroy {
    private subscriptions: Subscription[] = [];
    private hasMediaSubscriptionsPermission: boolean = false;
    private hypeType: HypeType = null;

    constructor(
        private monitoringRoutingService: MonitoringRoutingService,
        private sidebarService: MonitoringLeftSidebarService,
        private folderService: FolderService,
        private mediaSubscriptionService: MediaSubscriptionService,
        private currentUserStateService: CurrentUserStateService,
        private topicsService: TopicsService,
        private coreDialogService: CoreDialogService
    ) {
        super();

        this.subscriptions.push(
            this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.mediaSubscriptions]).subscribe(
                (provisioned) => {
                    this.hasMediaSubscriptionsPermission = provisioned;

                    if (this.hypeType === HypeType.Subscriptions) {
                        this.initializeSubscriptionsHype();
                    }
                }
            ),
            this.monitoringRoutingService.getCurrentRoute().subscribe(
                (route) => {
                    const hypeType = routeToHypeMap.get(route.type);
                    if (this.hypeType === hypeType) {
                        // already the same one, so no changes necessary
                        return;
                    }

                    this.hypeType = hypeType;
                    this.initializeHype();
                }
            ),
            this.currentUserStateService.getAccount().subscribe(account => {
                if (account) {
                    // handle race condition
                    if (this.hypeType !== null) {
                        this.initializeHype();
                    }
                }
            })
        );
    }

    private initializeHype(): void {
        switch (this.hypeType) {
            case HypeType.SavedCoverage:
                this.initializeSavedCoverageHype();
                break;
            case HypeType.Subscriptions:
                this.initializeSubscriptionsHype();
                break;
            case HypeType.Briefings:
                this.initializeBriefingsHype();
                break;
            case HypeType.Alerts:
                this.initializeAlertsHype();
                break;
            case HypeType.Reports:
                this.initializeReportsHype();
                break;
            case HypeType.Topics:
            default:
                this.initializeTopicHype();
                break;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
    }

    private initializeTopicHype(): void {
        this.monitoringRoutingService.getCurrentRoute().pipe(take(1)).subscribe(route => {
            const isNotFound = route &&
                              route.queryParamMap &&
                              route.queryParamMap.has('notFound');
            if (isNotFound) {
                // Not found configuration
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-topics',
                    headingText: 'Monitor.results.topicNotFoundDialog.heading',
                    line1Text: 'Monitor.results.topicNotFoundDialog.message',
                    line2Text: '',
                    linkText: 'Monitor.results.topicNotFoundDialog.acknowledge',
                    isNotFound: true
                };
            } else {
                if (this.currentUserStateService.agenticFeatureEnabled()) {
                    this.hypeConfig = {
                        icon: 'fa-kit-duotone fa-agpr-hype-topics',
                        headingText: 'Monitor.hype.topics.headline',
                        line1Text: 'Monitor.hype.topics.line1',
                        linkText: 'Monitor.hype.topics.linkNatural',
                        naturalLanguageBannerTitle: 'Monitor.hype.topics.naturalLanguageBannerTitle',
                        naturalLanguageSubmitTooltip: 'Monitoring.search.prcopilot.submitTooltip',
                        naturalLanguageSubmitTooltipEmpty: 'Monitoring.search.prcopilot.submitTooltipEmpty',
                        naturalLanguage: true,
                        hasAgentDebug: this.currentUserStateService.debugTopicAgenticWorkflowEnabled()
                    };
                } else {
                    this.hypeConfig = {
                        icon: 'fa-kit-duotone fa-agpr-hype-topics',
                        headingText: 'Monitor.hype.topics.headline',
                        line1Text: 'Monitor.hype.topics.line1',
                        line2Text: 'Monitor.hype.topics.line2',
                        linkText: 'Monitor.hype.topics.link'
                    };
                }
            }

            this.hypeConfigProvider.next(this.hypeConfig);
        });
    }

    showContextTakeover(naturalLanguagePrompt: string, onClose: (refined: string) => void): void {
        if (this.hypeType === HypeType.Briefings) {
            this.openCanvasAgentDialog(naturalLanguagePrompt, onClose);
            return;
        }
        this.topicsService.openTopicAgentDialog(
            naturalLanguagePrompt,
            topicAgentSystemPrompt,
            null,
            null,
            (apiTopic) => this.topicsService.navigateToNewSearchWithTopic(apiTopic),
            onClose
        );
    }

    private openCanvasAgentDialog(
        naturalLanguagePrompt: string,
        onDialogClose?: (refinedPurpose: string) => void
    ): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        this.coreDialogService.open(AIContextTakeoverComponent, {
            inputs: {
                title: 'Briefings.newBriefing.agilityAgent.contextTakeoverTitle',
                originalPurposeLabel: 'Briefings.newBriefing.prcopilot.originalPurpose',
                refinedPurposeLabel: 'Briefings.newBriefing.prcopilot.refinedPurpose',
                descriptionLabel: 'Briefings.newBriefing.prcopilot.descriptionLineOne',
                submitLabel: 'Briefings.newBriefing.prcopilot.updateCanvasPurpose',
                applyLabel: 'Briefings.newBriefing.prcopilot.create',
                naturalLanguagePrompt,
                taskDescription: canvasAgentSystemPrompt,
                overlayContainer: document.getElementById('main-content'),
                animationTrigger
            },
            outputs: {
                dialogClose: (refinedPurpose: string) => {
                    if (refinedPurpose) {
                        onDialogClose?.(refinedPurpose);
                        this.monitoringRoutingService.gotoNewCanvas(refinedPurpose);
                    }
                }
            }
        });
    }
    private initializeSavedCoverageHype(): void {
        this.monitoringRoutingService.getCurrentRoute().pipe(take(1)).subscribe(route => {
            const isNotFound = route &&
                              route.queryParamMap &&
                              route.queryParamMap.has('notFound');
            if (isNotFound) {
                // Not found configuration
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-coverage',
                    headingText: 'Monitor.results.folderNotFoundDialog.heading',
                    line1Text: 'Monitor.results.folderNotFoundDialog.message',
                    line2Text: '',
                    linkText: 'Monitor.results.savedCoverageNotFoundDialog.acknowledge',
                    isNotFound: true
                };
            } else {
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-coverage',
                    headingText: 'Monitor.hype.savedCoverage.headline',
                    line1Text: 'Monitor.hype.savedCoverage.line1',
                    line2Text: 'Monitor.hype.savedCoverage.line2',
                    linkText: 'Monitor.hype.savedCoverage.link'
                };
            }
            this.hypeConfigProvider.next(this.hypeConfig);
        });
    }
    private initializeSubscriptionsHype(): void {
        this.monitoringRoutingService.getCurrentRoute().pipe(take(1)).subscribe(route => {
            const isNotFound = route &&
                              route.queryParamMap &&
                              route.queryParamMap.has('notFound');
            if (isNotFound) {
                // Not found configuration
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-subscriptions',
                    headingText: 'Monitor.results.mediaSubscriptionNotFoundDialog.heading',
                    line1Text: 'Monitor.results.mediaSubscriptionNotFoundDialog.message',
                    line2Text: '',
                    linkText: 'Monitor.results.subscriptionNotFoundDialog.acknowledge',
                    isNotFound: true
                };
            } else if (this.hasMediaSubscriptionsPermission) {
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-subscriptions',
                    headingText: 'Monitor.hype.subscriptions.headline',
                    line1Text: 'Monitor.hype.subscriptions.line1',
                    line2Text: 'Monitor.hype.subscriptions.line2',
                    linkText: 'Monitor.hype.subscriptions.link'
                };
            } else {
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-subscriptions',
                    headingText: 'Monitor.hype.subscriptions.sales.headline',
                    line1Text: 'Monitor.hype.subscriptions.sales.line1',
                    line2Text: '',
                    line2TextAfterLink: 'Monitor.hype.subscriptions.sales.line2.afterLink',
                    linkText: 'Monitor.hype.subscriptions.sales.link',
                    linkHref: 'Monitor.hype.subscriptions.sales.link.href'
                };
            }
            this.hypeConfigProvider.next(this.hypeConfig);
        });
    }

    private initializeBriefingsHype(): void {
        this.monitoringRoutingService.getCurrentRoute().pipe(take(1)).subscribe(route => {
            const isNotFound = route &&
                              route.queryParamMap &&
                              route.queryParamMap.has('notFound');

            if (isNotFound) {
                // Not found configuration
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-briefings',
                    headingText: 'Monitor.results.briefingNotFoundDialog.heading',
                    line1Text: 'Monitor.results.briefingNotFoundDialog.message',
                    line2Text: '',
                    linkText: 'Monitor.results.briefingNotFoundDialog.acknowledge',
                    // secondaryLinkText: 'Monitor.results.briefingNotFoundDialog.acknowledge',
                    isNotFound: true
                };
            } else {
                // Normal configuration
                this.hypeConfig = this.currentUserStateService.agenticFeatureEnabled() ? {
                    icon: 'fa-kit-duotone fa-agpr-hype-briefings',
                    headingText: 'Monitor.hype.briefings.headline',
                    line1Text: 'Monitor.hype.briefings.line1',
                    line2Text: 'Monitor.hype.briefings.multiColumn.line2',
                    line2Bold: true,
                    naturalLanguage: true,
                    naturalLanguageBannerTitle: 'Monitor.hype.briefings.naturalLanguage.canvasDescription',
                    naturalLanguagePlaceholder: 'Monitor.hype.briefings.naturalLanguage.canvasPromptPlaceholder',
                    naturalLanguageSubmitTooltip: 'Briefings.newBriefing.prcopilot.submitTooltip',
                    naturalLanguageSubmitTooltipEmpty: 'Briefings.newBriefing.prcopilot.submitTooltipEmpty',
                    naturalLanguageTabIcon: 'fa-duotone fa-regular fa-frame',
                    naturalLanguageTabLabel: 'Monitor.hype.briefings.canvas',
                    linkText: 'Monitor.hype.briefings.naturalLanguage.createCanvasManually',
                    secondaryIcon: 'fa-regular fa-duotone fa-envelope',
                    secondaryHeadingText: 'Monitor.hype.briefings.emailBriefing',
                    secondaryLineText: 'Monitor.hype.briefings.newEmailBriefing.description',
                    secondaryLinkText: 'Monitor.hype.briefings.newEmailBriefing',
                } : {
                    icon: 'fa-kit-duotone fa-agpr-hype-briefings',
                    headingText: 'Monitor.hype.briefings.headline',
                    line1Text: 'Monitor.hype.briefings.line1',
                    line2Text: 'Monitor.hype.briefings.multiColumn.line2',
                    line2Bold: true,
                    firstIcon: 'fa-duotone fa-regular fa-frame',
                    secondaryIcon: 'fa-regular fa-duotone fa-envelope',
                    firstHeadingText: 'Monitor.hype.briefings.canvas',
                    secondaryHeadingText: 'Monitor.hype.briefings.emailBriefing',
                    firstLineText: 'Monitor.hype.briefings.newCanvas.description',
                    secondaryLineText: 'Monitor.hype.briefings.newEmailBriefing.description',
                    firstLinkText: 'Monitor.hype.briefings.newCanvas',
                    secondaryLinkText: 'Monitor.hype.briefings.newEmailBriefing',
                    multiColumns: true
                };
            }
            this.hypeConfigProvider.next(this.hypeConfig);
        });
    }
    private initializeAlertsHype(): void {
        this.monitoringRoutingService.getCurrentRoute().pipe(take(1)).subscribe(route => {
            const isNotFound = route &&
                              route.queryParamMap &&
                              route.queryParamMap.has('notFound');
            if (isNotFound) {
                // Not found configuration
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-alerts',
                    headingText: 'Monitor.results.coverageAlertNotFoundDialog.heading',
                    line1Text: 'Monitor.results.coverageAlertNotFoundDialog.message',
                    line2Text: '',
                    linkText: 'Monitor.results.alertNotFoundDialog.acknowledge',
                    isNotFound: true
                };
            } else {
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-alerts',
                    headingText: 'Monitor.hype.alerts.headline',
                    line1Text: 'Monitor.hype.alerts.line1',
                    line2Text: 'Monitor.hype.alerts.line2',
                    linkText: 'Monitor.hype.alerts.link'
                };
            }

            this.hypeConfigProvider.next(this.hypeConfig);
        });
    }
    private initializeReportsHype(): void {
        this.monitoringRoutingService.getCurrentRoute().pipe(take(1)).subscribe(route => {
            const isNotFound = route &&
                              route.queryParamMap &&
                              route.queryParamMap.has('notFound');
            if (isNotFound) {
                // Not found configuration
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-reports',
                    headingText: 'Monitor.results.reportNotFoundDialog.heading',
                    line1Text: 'Monitor.results.reportNotFoundDialog.message',
                    line2Text: '',
                    linkText: 'Monitor.results.reportNotFoundDialog.acknowledge',
                    isNotFound: true
                };
            } else {
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-reports',
                    headingText: 'Monitor.hype.reports.headline',
                    line1Text: 'Monitor.hype.reports.line1',
                    line2Text: 'Monitor.hype.reports.line2',
                    linkText: 'Monitor.hype.reports.link'
                };
            }
            this.hypeConfigProvider.next(this.hypeConfig);
        });
    }

    buttonClicked() {
        switch (this.hypeType) {
            case HypeType.SavedCoverage:
                this.folderService.openNewFolderDialog();
                break;
            case HypeType.Subscriptions:
                if (!this.hasMediaSubscriptionsPermission) {
                    return;
                }
                this.mediaSubscriptionService.navigateToNewMediaSubscription();
                break;
            case HypeType.Briefings:
                this.sidebarService.goToNewOnlineBriefing();
                break;
            case HypeType.Alerts:
                this.sidebarService.showAlertAmplifyDialog();
                break;
            case HypeType.Topics:
            default:
                this.sidebarService.showTopicAmplifyDialog();
                break;
        }
    }

    secondaryButtonClicked(): void {
        switch (this.hypeType) {
            case HypeType.Briefings:
                this.sidebarService.showBriefingAmplifyDialog();
                break;
        }
    }
}
